import { useRef, useState, ReactNode, SyntheticEvent, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { LOGIN } from '@/contexts/auth-reducer/actions';

// project-imports
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import Avatar from '@/components/base-component/extended/Avatar';
import MainCard from '@/components/base-component/MainCard';
import Transitions from '@/components/base-component/extended/Transitions';
import IconButton from '@/components/base-component/extended/IconButton';

import { ThemeMode } from '@/config';
import useAuth from '@/hooks/useAuth';
import authReducer from '@/contexts/auth-reducer/auth';

// assets
import avatarHolder from '@/assets/images/avatar-holder.png';
import { Logout } from 'iconsax-react';
import { Account } from '@/types/bs-types/data-types/account';
import useApiUpdate from '@/hooks/useApiUpdate';
import { Button, Divider } from '@mui/material';
import { getUrlAvatarPath, getUrlPath } from '@/utils/Path';
// import EditModal from '@/pages/dashboard/users/component/modal/edit-modal';
import { FormattedMessage } from 'react-intl';
import EditModal from '@/pages/dashboard/admin-users/component/modal/edit-modal';
import { getPermissionAdminsDisplay, getPermissionUsersDisplay, getUserTypeDisplay } from '@/utils/DataMapping';
import useAlert from '@/hooks/useAlert';
import useApiGet from '@/hooks/useApiGet';

// types
interface TabPanelProps {
  children?: ReactNode;
  dir?: string;
  index: number;
  value: number;
}

// tab panel wrapper
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
      sx={{ p: 1 }}
    >
      {value === index && children}
    </Box>
  );
}

// function a11yProps(index: number) {
//   return {
//     id: `profile-tab-${index}`,
//     'aria-controls': `profile-tabpanel-${index}`
//   };
// }

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};
// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function ProfilePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { logout, user } = useAuth();
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const handleOpenEdit = () => setOpenEdit((prevent) => !prevent);
  const handleCloseEdit = () => setOpenEdit(false);
  const [activeAccount, setActiveAccount] = useState<Account | undefined>(user?.activeAccount ?? undefined);
  const [unActiveAccounts, setUnActiveAccounts] = useState<Account[] | undefined>(
    user?.unActiveAccounts?.filter((value) => value.isActive) ?? undefined
  );
  const { data, handleRefresh } = useApiGet(`/account/${user?.activeAccount?.id}`);
  const { handleUpdate, error: errorUpdated } = useApiUpdate(`/auth/switch/${user?.id}`);

  const handleLogoutConfirm = () => {
    showAlert({
      type: 'Confirm',
      title: 'ยืนยันการออกจากระบบ',
      message: 'คุณแน่ใจหรือไม่ว่าต้องการออกจากระบบ?',
      confirmButtonColor: theme.palette.error.main,
      onCancel() {},
      onConfirm() {
        handleLogout();
      }
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleModalClose = () => setOpen(false);

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSwitchAccount = (id: string) => {
    if (id) {
      // find data id
      const newActiveAccount = unActiveAccounts?.filter((unActiveAccount) => unActiveAccount.id === id);
      // remove unActive === account
      const updateUnActives = user?.Accounts?.filter((account) => account.isActive && account.id !== newActiveAccount?.[0].id);
      setActiveAccount(newActiveAccount?.[0]);
      setUnActiveAccounts(updateUnActives);
      handleUpdate({ accountId: newActiveAccount?.[0].id });
      // delay 1s for change account
      setTimeout(() => {
        navigate(getUrlPath('/'));
        window.location.reload();
      }, 1000);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          borderRadius: 1,
          '&:hover': { bgcolor: theme.palette.mode === ThemeMode.DARK ? 'neutral.light' : 'neutral.lighter' },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.neutral.dark}`,
            outlineOffset: 2
          }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Avatar alt="profile user" src={data?.avatar ? getUrlAvatarPath(data?.avatar) : avatarHolder} />
        {/* <img src={'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRXJA32WU4rBpx7maglqeEtt3ot1tPIRWptxA&s'} width={40} height={40} /> */}
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: 290,
                minWidth: 240,
                maxWidth: 290,
                [theme.breakpoints.down('md')]: { maxWidth: 250 },
                borderRadius: 1.5
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3, padding: '24px 24px 10px 24px' }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          {/* <Avatar alt="profile user" src={activeAccount?.avatar ?? avatar1} size="lg" /> */}
                          <Avatar alt="profile user" src={data?.avatar ? getUrlAvatarPath(data?.avatar) : avatarHolder} size="lg" />
                          <Stack>
                            <Typography variant="subtitle1">{data?.fullName}</Typography>
                            <Typography variant="body1" color="neutral">
                              {data?.userRole === 'USER'
                                ? getPermissionUsersDisplay(data.permission)
                                : getPermissionAdminsDisplay(data?.permission)}
                            </Typography>
                            {activeAccount?.userRole !== 'USER' && (
                              <Typography variant="body2" color="disabled">
                                ( {getUserTypeDisplay(data?.userRole)} )
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item>
                        <Tooltip title={<FormattedMessage id="logout" />}>
                          <IconButton size="large" color="error" sx={{ p: 1 }} onClick={handleLogoutConfirm}>
                            <Logout variant="Bulk" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                    <Divider sx={{ bgcolor: 'white', marginTop: '10px' }} />
                  </CardContent>

                  {unActiveAccounts?.map((unActiveAccount) => (
                    <Tooltip title={<FormattedMessage id="switchAccount" />} key={`${unActiveAccount.id}`}>
                      <Button
                        key={`${unActiveAccount.id}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          color: 'black',
                          padding: '6px',
                          display: 'flex',
                          alignItems: 'start',
                          justifyContent: 'start',
                          textAlign: 'start',
                          ':hover': {
                            backgroundColor: 'lightgray',
                            cursor: 'pointer'
                          }
                        }}
                        color="info"
                        onClick={() => {
                          handleSwitchAccount(unActiveAccount?.id ?? '');
                        }}
                      >
                        <CardContent sx={{ padding: '0px 18px' }}>
                          <Grid container>
                            <Grid item>
                              <Stack direction="row" spacing={1.25} alignItems="start">
                                {/* <Avatar alt="profile user" src={unActiveAccount.avatar ?? avatar1} size="sm" /> */}
                                <Avatar
                                  alt="profile user"
                                  src={unActiveAccount?.avatar ? getUrlAvatarPath(unActiveAccount?.avatar) : avatarHolder}
                                  size="sm"
                                />
                                <Stack>
                                  <Typography variant="subtitle1" fontSize={12}>
                                    {unActiveAccount.fullName}
                                  </Typography>
                                  <Typography variant="body1" color="neutral" fontSize={11}>
                                    {unActiveAccount.userRole === 'USER'
                                      ? getPermissionUsersDisplay(unActiveAccount.permission)
                                      : getPermissionAdminsDisplay(unActiveAccount.permission)}{' '}
                                    {unActiveAccount.userRole !== 'USER' && `(${unActiveAccount.userRole})`}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Button>
                    </Tooltip>
                  ))}

                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab id={user?.activeAccount?.id} onClose={handleModalClose} onOpenModal={handleOpenEdit} />
                  </TabPanel>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      <EditModal
        data={data ?? {}}
        open={openEdit}
        onClose={handleCloseEdit}
        onSuccess={() => {
          handleCloseEdit();
          handleRefresh();
          window.location.reload();
        }}
      />
    </Box>
  );
}
